/**
 * Checks whether a string is non-null, non-undefined, and contains non-whitespace content.
 *
 * @param str - The string to check.
 * @returns True if str is not null/undefined and not empty/whitespace-only.
 */
export function stringHasValue(str: string | null | undefined): boolean {
  return str !== null && str !== undefined && str.trim() !== '';
}

/**
 * Converts a simple English plural word to its singular form using basic suffix rules (e.g. "-ies" -> "-y", trailing "s" removed).
 *
 * @param word - The word to convert, expected to be in plural form.
 * @returns The singular form of the word if a recognized plural pattern matched, otherwise the original word.
 */
export function convertToSingular(word) {
  const pluralForms = [
    [/([^aeiouy]|qu)ies$/i, '$1y'],
    [/s$/i, '']
  ];

  const isPlural = pluralForms.some(([pattern]) => (pattern as RegExp).test(word));

  if (isPlural) {
    for (let i = 0; i < pluralForms.length; i++) {
      const [pattern, replacement] = pluralForms[i];
      if ((pattern as RegExp).test(word)) {
        return word.replace(pattern, replacement);
      }
    }
  }
  return word;
}

/**
 * Extracts the contents of the first parenthesis group found in a string.
 *
 * @param str - The string to search for parenthesized content.
 * @returns The text inside the first pair of parentheses, or null if none found.
 */
export function extractParenthesisContents(str) {
  const regex = /\(([^)]+)\)/;
  const match = str?.match(regex);
  return match ? match[1] : null;
}

/**
 * Truncates a string to a maximum length, appending "..." when truncation occurs.
 *
 * @param str - The string to truncate.
 * @returns The original string if within maxLength, otherwise a truncated string ending in "...".
 */
export function truncateString(str: string, maxLength: number) {
  if (str.length > maxLength) {
    return str.substring(0, maxLength - 3) + '...';
  } else {
    return str;
  }
}

/**
 * Removes all HTML tags from a rich text string, replacing each tag with a space.
 *
 * @param str - Rich text/HTML string to strip tags from.
 * @returns The string with all HTML tags replaced by a single space.
 */
export const removeHTMLTagsFromRichText = (str: string) => {
  return str.replace(/(<([^>]+)>)/gi, ' ');
};

/**
 * Converts an HTML string to plain text by parsing it with DOMParser and reading the body's text content.
 *
 * @param html - HTML markup to convert.
 * @returns The plain-text content of the parsed HTML document body.
 */
export function htmlToPlainText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Checks whether a string appears to contain HTML markup (a matched opening/closing tag pair).
 *
 * @param noteBody - The note/text content to check.
 * @returns True if the string contains a matching HTML tag pair.
 */
export const checkIfStringIsRichText = (noteBody: string) => {
  const regexForHTML = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/;
  const isRichText = regexForHTML.test(noteBody);
  return isRichText;
};

/**
 * Parses a string into a number, returning a fallback value when the string is undefined or not parseable.
 *
 * @param text - The string to parse as a number, or undefined.
 * @returns The parsed float value, or fallback if text is undefined or not a valid number.
 */
export const convertStringOrUndefinedToNumber = (text: string | undefined, fallback: number) => {
  return text && parseFloat(text) ? parseFloat(text) : fallback;
};

/**
 * Uppercases the first character of a string, leaving the remaining characters as-is.
 *
 * @param str - The string whose first character should be uppercased.
 * @returns The string with only its first character uppercased; the rest is left unchanged.
 */
export const capitalize = (str: string) => {
  const [a, ...rest] = str.split('');
  return [a.toUpperCase(), ...rest].join('');
};

/**
 * Uppercases the first letter of a string and lowercases the remainder.
 *
 * @param str - The string to capitalize.
 * @returns The string with its first letter uppercased and the rest lowercased; empty string if falsy input.
 */
export const capitalizeFirstLetterOnly = (str: string) => {
    if (!str) return '';

    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Counts how many times a given substring/pattern occurs within a string.
 *
 * @param text - The text to search within.
 * @returns The number of times wordToMatch occurs in text.
 */
export const countOccurrencesOfString = (text: string, wordToMatch: string) => {
    const pattern = new RegExp(wordToMatch, 'g');
    const matches = text.match(pattern);
    return matches ? matches.length : 0;
};

/**
 * Collapses consecutive repeated occurrences (2 or more) of a given character down to a single occurrence.
 *
 * @param inputString - The string to clean up.
 * @returns The input string with runs of 2+ consecutive occurrences of characterToRemove collapsed to a single occurrence.
 */
export const removeRedundantCharacter = (inputString: string, characterToRemove: string) => {
    const pattern = new RegExp(`${characterToRemove}{2,}`, 'g');
    return inputString.replace(pattern, characterToRemove);
};

/**
 * Strips all characters except digits, periods, and a leading minus sign, removing any hyphen that immediately follows a digit.
 *
 * @param input - The string to sanitize.
 * @returns A string containing only digits, periods, and leading minus signs.
 */
export const sanitizeString = (input: string) => {
    const pattern = /[^0-9.\-]+/g;
    const sanitizedString = input.replace(pattern, '');
    return sanitizedString.replace(/(?<=\d)-/g, '');
};

/**
 * Fixes anchor tags whose href points to a Proofpoint URL Defense wrapper (urldefense.com) by extracting and displaying the original underlying URL as the link text.
 *
 * @param htmlString - HTML string potentially containing urldefense.com-wrapped links.
 * @returns The HTML string with urldefense.com-wrapped anchor link text replaced by the original unwrapped URL.
 */
export const urlDefenceMarkupFix = (htmlString: string): string => {
  // Create a DOM parser
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  // Get all anchor tags
  const anchorTags = doc.querySelectorAll('a');

  anchorTags.forEach(anchor => {
    const href = anchor.getAttribute('href');
    if (href && href.includes('urldefense.com')) {
      // Extract the URL between the "__" strings
      const [urlMatch, link] = [...href.match(/__([^_]+)__/) || []];
      if (urlMatch && link) {
        const newUrl = link;
        anchor.textContent = newUrl;
      }
    }
  });

  // Serialize the document back to a string
  return doc.body.innerHTML;
};

/**
 * Validates whether a string looks like a well-formed email address.
 *
 * @param email - The email address string to validate.
 * @returns True if the string matches a basic email address pattern.
 */
export const isValidEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
}

/**
 * Validates whether a string looks like a well-formed phone number.
 *
 * @param phone - The phone number string to validate.
 * @returns True if the string matches a basic phone number pattern.
 */
export const isValidPhone = (phone: string) => {
    const phoneRegex = /^\+?(\d[\d\s-]*\(?\d{3}\)?[\d\s-]{4,12}\d)$/;
    return phoneRegex.test(phone);
}

/** Minor connector words kept lowercase by `titleCase`, unless they're the very first word. */
const TITLE_CASE_MINOR_WORDS = new Set(['of', 'the', 'and', 'in', 'de', 'la', 'du', 'da']);

/**
 * Title-cases a free-text place name (e.g. a GIS layer's raw
 * country/state/political-division attribute, which may arrive in ALL CAPS,
 * all lowercase, or otherwise inconsistent casing). Capitalizes the first
 * letter of each word (splitting on spaces and hyphens) and lowercases the
 * rest, except a small set of minor connector words which stay lowercase
 * unless they're the first word overall — e.g. `"UNITED KINGDOM"` ->
 * `"United Kingdom"`, `"isle of man"` -> `"Isle of Man"`.
 */
export function titleCase(str: string): string {
  if (!str) return str;
  return str
    .split(' ')
    .map((word, wordIndex) =>
      word
        .split('-')
        .map((segment, segmentIndex) => {
          if (!segment) return segment;
          const lower = segment.toLowerCase();
          if (TITLE_CASE_MINOR_WORDS.has(lower) && !(wordIndex === 0 && segmentIndex === 0)) return lower;
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join('-'),
    )
    .join(' ');
}
